import { useState } from 'react';
import { Paper, Popover, MenuItem, MenuList } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';

export type FilterMenuItem = {
  key: string;
  label: string;
  panelContent: React.ReactNode;
};

type FilterMenuButtonProps = {
  menuItems: FilterMenuItem[];
  disabled?: boolean;
};

const FilterMenuButton: React.FC<FilterMenuButtonProps> = ({ menuItems, disabled = false }) => {
  const [mainAnchor, setMainAnchor] = useState<null | HTMLElement>(null);
  const [subKey, setSubKey] = useState<string | null>(null);
  const [subAnchor, setSubAnchor] = useState<null | HTMLElement>(null);

  const handleMainOpen = (e: React.MouseEvent<HTMLElement>) => {
    setMainAnchor(e.currentTarget);
  };

  const handleMainClose = () => {
    setMainAnchor(null);
    setSubKey(null);
    setSubAnchor(null);
  };

  const handleSubOpen = (e: React.SyntheticEvent<HTMLElement>, key: string) => {
    setSubAnchor(e.currentTarget);
    setSubKey(key);
  };

  const handleSubClose = () => {
    setSubKey(null);
    setSubAnchor(null);
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<FilterListIcon />}
        onClick={handleMainOpen}
        disabled={disabled}
        sx={{ textTransform: 'none', fontSize: '14px' }}
      >
        Filter
      </Button>

      <Popover
        open={Boolean(mainAnchor)}
        anchorEl={mainAnchor}
        onClose={handleMainClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: -8 }}
        // The category panels open in a separate Popover (its own portal). Without
        // this, the main Popover's focus trap would pull focus back out of the
        // panel, making the filter checkboxes unreachable by keyboard.
        disableEnforceFocus
      >
        <Paper sx={{ minWidth: 160 }}>
          {/* MUI v9 requires MenuItems to be rendered inside a Menu or MenuList */}
          <MenuList disablePadding>
            {menuItems.map(({ key, label }) => (
              <MenuItem
                key={key}
                // Override MenuList's roving tabindex so every category stays in the
                // tab order once the menu opens.
                tabIndex={0}
                // Open on hover (mouse) as well as click / keyboard activation.
                // MenuItem is a button, so Enter and Space trigger onClick.
                onMouseEnter={(e) => handleSubOpen(e, key)}
                onClick={(e) => handleSubOpen(e, key)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleSubOpen(e, key);
                  }
                }}
                selected={subKey === key}
                aria-haspopup="true"
                aria-expanded={subKey === key}
                sx={{ pr: 0.5, display: 'flex', justifyContent: 'space-between' }}
              >
                {label}
                <KeyboardArrowRightIcon fontSize="small" />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popover>

      {menuItems.map(({ key, panelContent }) => (
        <Popover
          key={key}
          open={subKey === key}
          anchorEl={subAnchor}
          onClose={handleSubClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // ArrowLeft returns to the category list (Escape is handled by Popover).
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              handleSubClose();
            }
          }}
          sx={{ pointerEvents: 'none' }}
          slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
        >
          {panelContent}
        </Popover>
      ))}
    </>
  );
};

export default FilterMenuButton;
